import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  RefreshCw, BarChart3, Users, ChevronDown, ChevronUp, 
  Loader2, TrendingUp, Package, ArrowRight, ExternalLink
} from "lucide-react";

export default function ClientRequirementDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [dashboardStats, setDashboardStats] = useState({
    totalRequirements: 0,
    stageStats: [],
    clientStats: []
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showClientBreakdown, setShowClientBreakdown] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);

  // Fetch dashboard statistics
  const fetchDashboardStats = useCallback(async (showToast = false) => {
    if (showToast) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await axios.get(`${API}/zoho/client-requirements/dashboard-stats`);
      if (response.data.success) {
        setDashboardStats({
          totalRequirements: response.data.totalRequirements,
          stageStats: response.data.stageStats || [],
          clientStats: response.data.clientStats || []
        });
        if (showToast) {
          toast.success(`Dashboard refreshed with ${response.data.totalRequirements} requirements`);
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      toast.error("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Handle stage card click - navigate to Client Requirements with filter
  const handleStageClick = (stage) => {
    setSelectedStage(stage);
    // Navigate to Client Requirements page with stage filter in URL
    if (stage === "all") {
      navigate("/client-requirements");
    } else {
      navigate(`/client-requirements?stage=${encodeURIComponent(stage)}`);
    }
  };

  // Handle client row click - navigate to Client Requirements filtered by client
  const handleClientClick = (clientName) => {
    navigate(`/client-requirements?client=${encodeURIComponent(clientName)}`);
  };

  // Get color scheme for each stage
  const getStageColor = (stage, isSelected = false) => {
    const colors = {
      "New": { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300", highlight: "bg-blue-500" },
      "Under R&D": { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300", highlight: "bg-purple-500" },
      "Waiting for Feedback": { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-300", highlight: "bg-amber-500" },
      "Sample Ready": { bg: "bg-teal-100", text: "text-teal-700", border: "border-teal-300", highlight: "bg-teal-500" },
      "Sample Sent": { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-300", highlight: "bg-indigo-500" },
      "Feedback Received": { bg: "bg-green-100", text: "text-green-700", border: "border-green-300", highlight: "bg-green-500" },
      "Completed": { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300", highlight: "bg-emerald-500" },
      "On Hold": { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300", highlight: "bg-slate-500" },
    };
    return colors[stage] || { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300", highlight: "bg-slate-500" };
  };

  return (
    <div className="p-6 md:p-8" data-testid="client-requirement-dashboard-page">
      {/* Header */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-poppins font-bold text-slate-800">
              Client Requirement Dashboard
            </h1>
            <p className="text-slate-500 font-poppins mt-1">
              Job Stage overview and client-wise requirement breakdown
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => fetchDashboardStats(true)}
              disabled={refreshing}
              className="font-poppins"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
            <Button
              onClick={() => navigate('/client-requirements')}
              className="bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Full Report
            </Button>
          </div>
        </div>
      </motion.div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-vcc-teal" />
          <span className="ml-3 text-slate-500 font-poppins">Loading dashboard...</span>
        </div>
      ) : (
        <>
          {/* Summary Card */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="bg-gradient-to-r from-vcc-gold/20 via-vcc-teal/10 to-vcc-gold/20 rounded-2xl p-6 border border-vcc-gold/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/80 flex items-center justify-center shadow-sm">
                    <Package className="w-8 h-8 text-vcc-gold" />
                  </div>
                  <div>
                    <p className="text-4xl font-bold text-slate-800 font-poppins">
                      {dashboardStats.totalRequirements}
                    </p>
                    <p className="text-slate-600 font-poppins">Total Client Requirements</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-vcc-teal font-poppins">
                    {dashboardStats.clientStats.length} Clients
                  </p>
                  <p className="text-sm text-slate-500 font-poppins">
                    {dashboardStats.stageStats.length} Job Stages
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Job Stage Dashboard */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-vcc-gold/10 to-vcc-teal/10 px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-vcc-gold/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-vcc-gold" />
                  </div>
                  <div>
                    <h2 className="text-lg font-poppins font-semibold text-slate-800">
                      Job Stage Status
                    </h2>
                    <p className="text-sm text-slate-500 font-poppins">
                      Click on any status to view filtered requirements
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Status Cards Grid */}
              <div className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
                  {/* All Stages Card */}
                  <motion.div
                    onClick={() => handleStageClick("all")}
                    className="cursor-pointer rounded-xl p-4 border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white hover:border-vcc-teal hover:shadow-lg transition-all group"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3 group-hover:bg-vcc-teal/10 transition-colors">
                        <TrendingUp className="w-6 h-6 text-slate-600 group-hover:text-vcc-teal" />
                      </div>
                      <p className="text-3xl font-bold text-slate-800 font-poppins">
                        {dashboardStats.totalRequirements}
                      </p>
                      <p className="text-xs font-poppins text-slate-500 mt-1">All Stages</p>
                    </div>
                    <div className="mt-3 flex items-center justify-center gap-1 text-xs text-vcc-teal opacity-0 group-hover:opacity-100 transition-opacity">
                      <span>View All</span>
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  </motion.div>
                  
                  {/* Individual Stage Cards */}
                  {dashboardStats.stageStats.map((stat, index) => {
                    const colors = getStageColor(stat.stage);
                    return (
                      <motion.div
                        key={stat.stage}
                        onClick={() => handleStageClick(stat.stage)}
                        className={`cursor-pointer rounded-xl p-4 border-2 ${colors.border} ${colors.bg} hover:shadow-lg transition-all group`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + index * 0.05 }}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="text-center">
                          <div className={`w-3 h-3 mx-auto rounded-full ${colors.highlight} mb-3`} />
                          <p className={`text-3xl font-bold ${colors.text} font-poppins`}>
                            {stat.count}
                          </p>
                          <p className="text-xs font-poppins text-slate-600 mt-1 truncate" title={stat.stage}>
                            {stat.stage}
                          </p>
                        </div>
                        <div className={`mt-3 flex items-center justify-center gap-1 text-xs ${colors.text} opacity-0 group-hover:opacity-100 transition-opacity`}>
                          <span>Filter</span>
                          <ArrowRight className="w-3 h-3" />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Client-wise Breakdown */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setShowClientBreakdown(!showClientBreakdown)}
                className="w-full bg-gradient-to-r from-vcc-teal/5 to-vcc-gold/5 px-6 py-4 border-b border-slate-200 flex items-center justify-between hover:from-vcc-teal/10 hover:to-vcc-gold/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-vcc-teal/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-vcc-teal" />
                  </div>
                  <div className="text-left">
                    <h2 className="text-lg font-poppins font-semibold text-slate-800">
                      Client-wise Breakdown
                    </h2>
                    <p className="text-sm text-slate-500 font-poppins">
                      {dashboardStats.clientStats.length} clients with requirement data
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-poppins">
                    {dashboardStats.clientStats.length} Clients
                  </Badge>
                  {showClientBreakdown ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>
              
              {showClientBreakdown && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-50 z-10">
                        <TableRow>
                          <TableHead className="font-poppins font-semibold">Client Name</TableHead>
                          <TableHead className="font-poppins font-semibold text-center">Total</TableHead>
                          {dashboardStats.stageStats.map((stat) => (
                            <TableHead 
                              key={stat.stage} 
                              className="font-poppins text-xs text-center min-w-[80px]"
                              title={stat.stage}
                            >
                              {stat.stage.length > 12 ? stat.stage.substring(0, 12) + "..." : stat.stage}
                            </TableHead>
                          ))}
                          <TableHead className="font-poppins text-center">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboardStats.clientStats.map((client, index) => (
                          <TableRow 
                            key={client.clientName}
                            className="hover:bg-slate-50 transition-colors"
                          >
                            <TableCell className="font-poppins font-medium">
                              <div>
                                <p className="text-slate-800">{client.clientName}</p>
                                {client.companyName && client.companyName !== client.clientName && (
                                  <p className="text-xs text-slate-400">{client.companyName}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className="bg-vcc-teal/10 text-vcc-teal border-vcc-teal/30 font-poppins font-bold">
                                {client.total}
                              </Badge>
                            </TableCell>
                            {dashboardStats.stageStats.map((stat) => {
                              const count = client.stages[stat.stage] || 0;
                              const colors = getStageColor(stat.stage);
                              return (
                                <TableCell key={stat.stage} className="text-center">
                                  {count > 0 ? (
                                    <Badge variant="secondary" className={`${colors.bg} ${colors.text} font-poppins text-xs`}>
                                      {count}
                                    </Badge>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleClientClick(client.clientName)}
                                className="text-vcc-teal hover:text-vcc-teal/80 hover:bg-vcc-teal/10 font-poppins text-xs"
                              >
                                View
                                <ArrowRight className="w-3 h-3 ml-1" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div 
                onClick={() => navigate('/client-requirements')}
                className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm cursor-pointer hover:shadow-md hover:border-vcc-teal/50 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-poppins font-semibold text-slate-800">View All Requirements</p>
                    <p className="text-sm text-slate-500 font-poppins">Full requirement list</p>
                  </div>
                </div>
              </div>
              
              <div 
                onClick={() => navigate('/client-requirement')}
                className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm cursor-pointer hover:shadow-md hover:border-vcc-gold/50 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-vcc-gold/20 flex items-center justify-center group-hover:bg-vcc-gold/30 transition-colors">
                    <TrendingUp className="w-6 h-6 text-vcc-gold" />
                  </div>
                  <div>
                    <p className="font-poppins font-semibold text-slate-800">New Requirement</p>
                    <p className="text-sm text-slate-500 font-poppins">Submit new client request</p>
                  </div>
                </div>
              </div>
              
              <div 
                onClick={() => navigate('/clients')}
                className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm cursor-pointer hover:shadow-md hover:border-vcc-teal/50 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-vcc-teal/10 flex items-center justify-center group-hover:bg-vcc-teal/20 transition-colors">
                    <Users className="w-6 h-6 text-vcc-teal" />
                  </div>
                  <div>
                    <p className="font-poppins font-semibold text-slate-800">Client List</p>
                    <p className="text-sm text-slate-500 font-poppins">View all clients</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
